import React, { useContext, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Chip,
  Avatar,
  Button,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  FitnessCenter as FitnessIcon,
  Restaurant as RestaurantIcon,
  Warning as WarningIcon,
  Scale as ScaleIcon,
  Height as HeightIcon,
  Cake as CakeIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { AuthContext } from '../../context/AuthContext';
import api from '../../services/api';
import { grey, primary, success, error as errorColor, radius, fontSize, fontWeight } from '../../styles/tokens';

/**
 * Profil Client - Mobile First
 *
 * Affiche les informations du compte client
 */
const ClientProfile = () => {
  const { user, logout } = useContext(AuthContext);
  const [coachInfo, setCoachInfo] = useState(null);

  // Charger les infos du coach
  useEffect(() => {
    const fetchCoachInfo = async () => {
      try {
        const response = await api.get('/client/profile/coach');
        setCoachInfo(response);
      } catch (err) {
        console.error('Erreur lors du chargement du coach:', err);
      }
    };
    if (user?.coach) {
      fetchCoachInfo();
    }
  }, [user]);

  // Traduction des preferences
  const translatePreference = (pref) => {
    const translations = {
      'vegetarian': 'Vegetarien',
      'vegan': 'Vegan',
      'gluten_free': 'Sans gluten',
      'gluten': 'Gluten',
      'lactose_free': 'Sans lactose',
      'lactose': 'Lactose',
      'halal': 'Halal',
      'kosher': 'Casher',
      'pescatarian': 'Pescetarien',
      'low_carb': 'Pauvre en glucides',
      'high_protein': 'Riche en proteines',
      'nuts': 'Fruits a coque',
      'eggs': 'Oeufs',
      'fish': 'Poisson',
      'shellfish': 'Crustaces',
      'soy': 'Soja',
      'peanuts': 'Arachides',
    };
    return translations[pref] || pref;
  };

  // Initiales pour avatar
  const getInitials = () => {
    if (!user) return '?';
    const first = user.firstName?.charAt(0) || '';
    const last = user.lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || '?';
  };

  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Verifier si les donnees physiques existent
  const hasPhysicalData = user?.weight || user?.height || user?.age;
  const hasMacros = user?.targetMacros?.calories;
  const hasPreferences = user?.dietaryPreferences?.length > 0 || user?.intolerances?.length > 0;

  return (
    <>
      <Helmet>
        <title>Mon Profil - Yoco</title>
      </Helmet>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Profile Header Card */}
        <Card sx={{ borderRadius: radius.lg, boxShadow: 'none', border: `1px solid ${grey[200]}` }}>
          <CardContent sx={{ pt: 3 }}>
            <Stack alignItems="center" spacing={1.5}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: primary[100],
                  color: primary[600],
                  fontSize: fontSize['2xl'],
                  fontWeight: fontWeight.bold,
                }}
              >
                {getInitials()}
              </Avatar>
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  sx={{
                    fontSize: fontSize.xl,
                    fontWeight: fontWeight.semibold,
                    color: grey[900],
                  }}
                >
                  {user?.firstName} {user?.lastName}
                </Typography>
                <Typography
                  sx={{
                    fontSize: fontSize.sm,
                    color: grey[500],
                  }}
                >
                  {user?.email}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Informations du compte */}
        <Card sx={{ borderRadius: radius.lg, boxShadow: 'none', border: `1px solid ${grey[200]}` }}>
          <CardContent>
            <Typography
              sx={{
                fontSize: fontSize.sm,
                fontWeight: fontWeight.semibold,
                color: grey[700],
                mb: 2,
              }}
            >
              Mon compte
            </Typography>

            <Stack spacing={2}>
              {/* Email */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1, bgcolor: grey[100], borderRadius: radius.md }}>
                  <EmailIcon sx={{ fontSize: 20, color: grey[500] }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: fontSize.xs, color: grey[500] }}>Email</Typography>
                  <Typography sx={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: grey[900] }}>
                    {user?.email || '-'}
                  </Typography>
                </Box>
              </Box>

              {/* Coach */}
              {coachInfo && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1, bgcolor: primary[50], borderRadius: radius.md }}>
                    <PersonIcon sx={{ fontSize: 20, color: primary[500] }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: fontSize.xs, color: grey[500] }}>Mon coach</Typography>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: grey[900] }}>
                      {coachInfo.firstName} {coachInfo.lastName}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Date d'inscription */}
              {user?.createdAt && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1, bgcolor: grey[100], borderRadius: radius.md }}>
                    <CalendarIcon sx={{ fontSize: 20, color: grey[500] }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: fontSize.xs, color: grey[500] }}>Membre depuis</Typography>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: grey[900] }}>
                      {formatDate(user.createdAt)}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Donnees physiques - si existantes */}
        {hasPhysicalData && (
          <Card sx={{ borderRadius: radius.lg, boxShadow: 'none', border: `1px solid ${grey[200]}` }}>
            <CardContent>
              <Typography
                sx={{
                  fontSize: fontSize.sm,
                  fontWeight: fontWeight.semibold,
                  color: grey[700],
                  mb: 2,
                }}
              >
                Mes donnees
              </Typography>

              <Stack direction="row" justifyContent="space-around">
                {user?.weight && (
                  <Stack alignItems="center" spacing={0.5}>
                    <ScaleIcon sx={{ fontSize: 24, color: grey[400] }} />
                    <Typography sx={{ fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: grey[900] }}>
                      {user.weight}
                    </Typography>
                    <Typography sx={{ fontSize: fontSize.xs, color: grey[500] }}>kg</Typography>
                  </Stack>
                )}
                {user?.height && (
                  <Stack alignItems="center" spacing={0.5}>
                    <HeightIcon sx={{ fontSize: 24, color: grey[400] }} />
                    <Typography sx={{ fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: grey[900] }}>
                      {user.height}
                    </Typography>
                    <Typography sx={{ fontSize: fontSize.xs, color: grey[500] }}>cm</Typography>
                  </Stack>
                )}
                {user?.age && (
                  <Stack alignItems="center" spacing={0.5}>
                    <CakeIcon sx={{ fontSize: 24, color: grey[400] }} />
                    <Typography sx={{ fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: grey[900] }}>
                      {user.age}
                    </Typography>
                    <Typography sx={{ fontSize: fontSize.xs, color: grey[500] }}>ans</Typography>
                  </Stack>
                )}
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Macros cibles - si existantes */}
        {hasMacros && (
          <Card sx={{ borderRadius: radius.lg, boxShadow: 'none', border: `1px solid ${grey[200]}` }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <FitnessIcon sx={{ fontSize: 20, color: primary[500] }} />
                <Typography
                  sx={{
                    fontSize: fontSize.sm,
                    fontWeight: fontWeight.semibold,
                    color: grey[700],
                  }}
                >
                  Objectifs nutritionnels
                </Typography>
              </Stack>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 1,
                  textAlign: 'center',
                }}
              >
                <Box sx={{ p: 1.5, bgcolor: primary[50], borderRadius: radius.md }}>
                  <Typography sx={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: primary[600] }}>
                    {user.targetMacros.calories}
                  </Typography>
                  <Typography sx={{ fontSize: fontSize.xs, color: grey[600] }}>kcal</Typography>
                </Box>
                <Box sx={{ p: 1.5, bgcolor: grey[50], borderRadius: radius.md }}>
                  <Typography sx={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: grey[700] }}>
                    {user.targetMacros.protein}g
                  </Typography>
                  <Typography sx={{ fontSize: fontSize.xs, color: grey[500] }}>Prot.</Typography>
                </Box>
                <Box sx={{ p: 1.5, bgcolor: grey[50], borderRadius: radius.md }}>
                  <Typography sx={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: grey[700] }}>
                    {user.targetMacros.carbs}g
                  </Typography>
                  <Typography sx={{ fontSize: fontSize.xs, color: grey[500] }}>Gluc.</Typography>
                </Box>
                <Box sx={{ p: 1.5, bgcolor: grey[50], borderRadius: radius.md }}>
                  <Typography sx={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: grey[700] }}>
                    {user.targetMacros.fats}g
                  </Typography>
                  <Typography sx={{ fontSize: fontSize.xs, color: grey[500] }}>Lip.</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Preferences et intolerances - si existantes */}
        {hasPreferences && (
          <Card sx={{ borderRadius: radius.lg, boxShadow: 'none', border: `1px solid ${grey[200]}` }}>
            <CardContent>
              {user?.dietaryPreferences?.length > 0 && (
                <Box sx={{ mb: user?.intolerances?.length > 0 ? 2 : 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <RestaurantIcon sx={{ fontSize: 18, color: success.main }} />
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: grey[700] }}>
                      Preferences
                    </Typography>
                  </Stack>
                  <Stack direction="row" flexWrap="wrap" gap={0.75}>
                    {user.dietaryPreferences.map((pref, index) => (
                      <Chip
                        key={index}
                        label={translatePreference(pref)}
                        size="small"
                        sx={{
                          bgcolor: success[50],
                          color: success.dark,
                          fontWeight: fontWeight.medium,
                          height: 26,
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {user?.intolerances?.length > 0 && (
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <WarningIcon sx={{ fontSize: 18, color: errorColor.main }} />
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: grey[700] }}>
                      Intolerances
                    </Typography>
                  </Stack>
                  <Stack direction="row" flexWrap="wrap" gap={0.75}>
                    {user.intolerances.map((intol, index) => (
                      <Chip
                        key={index}
                        label={translatePreference(intol)}
                        size="small"
                        sx={{
                          bgcolor: errorColor[50],
                          color: errorColor.dark,
                          fontWeight: fontWeight.medium,
                          height: 26,
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Bouton Deconnexion */}
        <Button
          fullWidth
          variant="outlined"
          color="inherit"
          startIcon={<LogoutIcon />}
          onClick={logout}
          sx={{
            py: 1.5,
            color: grey[600],
            borderColor: grey[300],
            borderRadius: radius.lg,
            fontWeight: fontWeight.medium,
            '&:hover': {
              bgcolor: grey[50],
              borderColor: grey[400],
            },
          }}
        >
          Se deconnecter
        </Button>
      </Box>
    </>
  );
};

export default ClientProfile;
